import { getApiEndpoints } from '@/services/api/endpoints';
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import type {
  CloneSchemaPayload,
  CreateSchemaPayload,
  ListSchemasResponse,
  UpdateSchemaPayload,
} from './schemasThunkTypes';
import type { CreateItemResponse, DeleteSchemaResponse } from '@/services/api/types';
import type { SchemaRecord, SchemaStatus } from '@/domain/entities/property-schema';

type ApiError = { message: string; status?: number; body?: string };

export const fetchSchemas = createAppAsyncThunk<ListSchemasResponse, { status: SchemaStatus }>(
  'propertySchemas/fetchByStatus',
  async ({ status }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      // await sleep(3000); // simulate latency
      const endpoint = `${getApiEndpoints().core.propertySchemas.listSchemas}?status=${encodeURIComponent(status)}`;
      return await apiService.get<ListSchemasResponse>(endpoint);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message } satisfies ApiError);
    }
  },
);

export const fetchSchemaById = createAppAsyncThunk<SchemaRecord, { id: string; version: string }>(
  'propertySchemas/fetchById',
  async ({ id, version }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;
    try {
      // /business/schemas?id=SCHEMA#...&version=HEAD|VER#1
      const endpoint =
        getApiEndpoints().core.propertySchemas.getSchema +
        `?id=${encodeURIComponent(id)}&version=${encodeURIComponent(version)}`;
      return await apiService.get<SchemaRecord>(endpoint);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message } satisfies ApiError);
    }
  },
);

export const createSchema = createAppAsyncThunk<CreateItemResponse<CreateSchemaPayload>, CreateSchemaPayload>(
  'propertySchemas/create',
  async (input, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;
    try {
      const endpoint = getApiEndpoints().core.propertySchemas.createSchema;
      return await apiService.put<CreateItemResponse<CreateSchemaPayload>>(endpoint, input);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message } satisfies ApiError);
    }
  },
);

export const updateSchema = createAppAsyncThunk<CreateItemResponse<UpdateSchemaPayload>, UpdateSchemaPayload>(
  'propertySchemas/update',
  async (input, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;
    try {
      const endpoint = getApiEndpoints().core.propertySchemas.updateSchema + '?id=' + encodeURIComponent(input.id);
      return await apiService.put<CreateItemResponse<UpdateSchemaPayload>>(endpoint, input);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message } satisfies ApiError);
    }
  },
);

export const deleteSchema = createAppAsyncThunk<DeleteSchemaResponse, { id: string }>(
  'propertySchemas/delete',
  async ({ id }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      // await sleep(2000); // simulate latency
      const endpoint = getApiEndpoints().core.propertySchemas.deleteSchema + `?id=${encodeURIComponent(id)}`;
      return await apiService.delete<DeleteSchemaResponse>(endpoint);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({
        message: e.message || 'Failed to delete schema',
      } satisfies ApiError);
    }
  },
);

export const publishSchema = createAppAsyncThunk<void, { id: string }>(
  'propertySchemas/publish',
  async ({ id }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = getApiEndpoints().core.propertySchemas.publishSchema + `?id=${encodeURIComponent(id)}`;

      await apiService.post(endpoint, null); // no body required
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message } satisfies ApiError);
    }
  },
);

export const cloneSchema = createAppAsyncThunk<CreateItemResponse<CloneSchemaPayload>, CloneSchemaPayload>(
  'propertySchemas/clone',
  async ({ id, version }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      // Assuming you added: /schemas/clone (POST) and it expects ?id=...&version=...
      const endpoint =
        getApiEndpoints().core.propertySchemas.cloneSchema +
        `?id=${encodeURIComponent(id)}&version=${encodeURIComponent(version)}`;

      return await apiService.post<CreateItemResponse<CloneSchemaPayload>>(endpoint, undefined);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message } satisfies ApiError);
    }
  },
);

export const deprecateSchema = createAppAsyncThunk<void, { id: string; version: string }>(
  'propertySchemas/deprecate',
  async ({ id, version }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint =
        getApiEndpoints().core.propertySchemas.deprecateSchema +
        `?id=${encodeURIComponent(id)}&version=${encodeURIComponent(version)}`;
      await apiService.post(endpoint);
    } catch (err) {
      const e = err as Error & { status?: number };
      return rejectWithValue({ message: e.message });
    }
  },
);
